<script lang="ts" setup>
import { Page } from "@/components/Ui";
import { FaqNav } from "@/components/Faq";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { computed, ref, watch } from "vue";
import { isMobile } from "@/helpers/breakpoints";
import { storeToRefs } from "pinia";
import { type TLocaleFaq, useFaqStore } from "@/store/faq";

const { fetchFaq } = useFaqStore();
const { faqItems, pending } = storeToRefs(useFaqStore());

const route = useRoute();
const { locale } = useI18n();
const isIndex = computed(() => {
  return route.meta.key === "faq-root";
});

const currentLocale = computed(() => {
  if (!locale.value) return null;
  const dir: { [key: string]: string } = { ua: "uk" };
  return (dir[locale.value] ? dir[locale.value] : locale.value) ?? null;
});

fetchFaq(currentLocale.value as TLocaleFaq);

useHead({
  htmlAttrs: {
    class: "faq",
  },
});
definePageMeta({
  page: "faq",
});

const router = useRouter();
const prevLocale = ref(`${locale.value}`);

watch(
  () => locale.value,
  () => {
    // If this page is /faq
    if (route.path.endsWith("/faq")) return;

    const newLocale = locale.value;
    const prevLocaleValue = prevLocale.value;

    const hash = `${route.hash ?? ""}`;
    const arr = route.path.split("/");

    let url;
    if (newLocale === "en") {
      // Cases: hi => en
      const slug = arr[3].replace(`${prevLocaleValue}-`, "");
      url = `/faq/${slug}${hash}`;
    } else {
      if (prevLocaleValue === "en") {
        // Cases: en => hi
        url = `/${newLocale}/faq/${newLocale}-${arr[2]}${hash}`;
      } else {
        // Cases: ru => hi
        const slug = arr[3].replace(`${prevLocaleValue}-`, "");
        url = `/${newLocale}/faq/${newLocale}-${slug}${hash}`;
      }
    }

    // Save prev value
    prevLocale.value = newLocale;

    router.push(url);
  },
);
</script>

<template>
  <Page
    :class="{
      [$style.root]: true,
      [$style.root__grid]: !isIndex,
      container: !isMobile,
    }"
  >
    <div
      v-if="!isIndex"
      class="pr-5 sticky top-[6rem] h-full hidden xl:block order-1"
    >
      <FaqNav
        :items="faqItems"
        :pending="pending"
      />
    </div>
    <main
      :class="{ container: isMobile }"
      class="order-2"
    >
      <NuxtPage />
    </main>
    <div
      v-if="!isIndex"
      id="faq-right-col"
      class="sticky top-[3.5rem] md:top-[6rem] h-fit order-1 md:order-3 max-w-full overflow-x-auto bg-white"
    />
  </Page>
</template>

<style lang="scss" module>
.root {
  @apply md:pt-10 pb-20  mt-[70px] md:mt-[90px];
  &__grid {
    @apply grid gap-5;

    @screen md {
      grid-template-columns: auto 213px;
    }
    @screen xl {
      grid-template-columns: 250px minmax(733px, 733px) auto;
    }
  }

  & main {
    @apply w-full max-w-[733px] mx-auto;
  }
}
</style>
